import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useWebSocket, NotificationChannel, NotificationType } from '@/contexts/WebSocketContext';
import { ScanProgressBanner } from '@/components/NotificationCenter';
import {
  Upload, GitBranch, Package, FileCode, Shield, AlertTriangle,
  CheckCircle, XCircle, Loader2, ArrowLeft, Copy, Wifi, Clock,
  History, RefreshCw, ChevronRight, Timer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  uploadDependencyFile,
  scanRepository,
  scanContainer,
  scanIaC,
  getSeverityColor,
  formatScanType,
  getScanProgress,
  getScanHistory,
  getScan
} from '@/services/litesabre';
import { getFrameworks } from '@/services/grc';

const LiteSabreScan = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('type') || 'upload';
  const { subscribe, addListener, isConnected, scanProgress } = useWebSocket();
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [scanning, setScanning] = useState(false);
  const [currentScanId, setCurrentScanId] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState('');
  
  // Progress tracking state
  const [progress, setProgress] = useState({ percentage: 0, eta_formatted: null });
  const progressIntervalRef = useRef(null);
  
  // Scan history state
  const [scanHistory, setScanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  
  // Form states
  const [file, setFile] = useState(null);
  const [repoUrl, setRepoUrl] = useState('');
  const [branch, setBranch] = useState('main');
  const [imageName, setImageName] = useState('');
  const [registry, setRegistry] = useState('');
  const [iacContent, setIacContent] = useState('');
  const [iacType, setIacType] = useState('terraform');
  const [iacFileName, setIacFileName] = useState('');
  
  // Load scan history on mount
  useEffect(() => {
    loadScanHistory();
  }, []);
  
  // Poll for progress when scanning
  useEffect(() => {
    if (scanning && currentScanId) {
      progressIntervalRef.current = setInterval(async () => {
        try {
          const progressData = await getScanProgress(currentScanId);
          setProgress({
            percentage: progressData.percentage || 0,
            eta_formatted: progressData.eta_formatted,
            status: progressData.status
          });
          
          // Check if completed or failed
          if (progressData.status === 'completed' || progressData.status === 'failed') {
            clearInterval(progressIntervalRef.current);
            setScanning(false);
            
            if (progressData.status === 'completed') {
              toast.success('Scan completed!');
              // Load full scan result
              const fullScan = await getScan(currentScanId);
              setScanResult(fullScan);
              loadScanHistory(); // Refresh history
            } else {
              // Show error message from scan
              const fullScan = await getScan(currentScanId);
              const errorMsg = fullScan?.error_message || 'Scan failed';
              toast.error(errorMsg);
              setScanResult(fullScan);
            }
          }
        } catch (err) {
          console.error('Failed to fetch progress:', err);
        }
      }, 2000);
    }
    
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [scanning, currentScanId]);
  
  const loadScanHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await getScanHistory(15);
      setScanHistory(data.history || []);
    } catch (err) {
      console.error('Failed to load scan history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };
  
  // Subscribe to LiteSaber channel for scan updates
  useEffect(() => {
    subscribe(NotificationChannel.LITESABER);
    
    // Listen for scan completion
    const cleanupCompleted = addListener(NotificationType.SCAN_COMPLETED, (data) => {
      if (data.channel === NotificationChannel.LITESABER && data.scan_id === currentScanId) {
        setScanning(false);
        setProgress({ percentage: 100, eta_formatted: null, status: 'completed' });
        toast.success('Scan completed!');
        // Refresh scan results
        if (data.summary) {
          setScanResult(prev => ({ ...prev, ...data.summary }));
        }
        loadScanHistory();
      }
    });
    
    // Listen for scan failure
    const cleanupFailed = addListener(NotificationType.SCAN_FAILED, (data) => {
      if (data.channel === NotificationChannel.LITESABER && data.scan_id === currentScanId) {
        setScanning(false);
        toast.error(data.error_message || 'Scan failed');
        loadScanHistory();
      }
    });
    
    return () => {
      cleanupCompleted();
      cleanupFailed();
    };
  }, [subscribe, addListener, currentScanId]);
  
  useEffect(() => {
    loadFrameworks();
  }, []);
  
  const loadFrameworks = async () => {
    try {
      const data = await getFrameworks();
      // Handle paginated response - extract items array
      const frameworkList = Array.isArray(data) ? data : (data.items || []);
      setFrameworks(frameworkList);
    } catch (err) {
      console.error('Failed to load frameworks:', err);
      setFrameworks([]); // Set empty array on error
    }
  };
  
  const handleFileDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer?.files[0] || e.target.files[0];
    if (droppedFile) {
      const supportedFiles = [
        'package-lock.json', 'yarn.lock', 'pnpm-lock.yaml',
        'requirements.txt', 'Pipfile.lock', 'poetry.lock',
        'composer.lock', 'Gemfile.lock', 'go.sum',
        'Cargo.lock', 'packages.lock.json'
      ];
      
      if (supportedFiles.includes(droppedFile.name)) {
        setFile(droppedFile);
      } else {
        toast.error(`Unsupported file. Supported: ${supportedFiles.join(', ')}`);
      }
    }
  };
  
  const handleUploadScan = async () => {
    if (!file) {
      toast.error('Please select a file to scan');
      return;
    }
    
    setScanning(true);
    setScanResult(null);
    
    try {
      const result = await uploadDependencyFile(file, null, selectedFramework || null);
      setScanResult(result);
      toast.success('Scan completed successfully!');
    } catch (err) {
      console.error('Scan failed:', err);
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };
  
  const handleRepoScan = async () => {
    if (!repoUrl) {
      toast.error('Please enter a repository URL');
      return;
    }
    
    setScanning(true);
    setScanResult(null);
    setProgress({ percentage: 0, eta_formatted: null, status: 'pending' });
    
    try {
      const result = await scanRepository(
        repoUrl,
        branch,
        ['dependency', 'iac'],
        selectedFramework || null
      );
      
      // Set current scan ID for progress tracking
      setCurrentScanId(result.id);
      
      // If scan returned completed (small repos), show result
      if (result.status === 'completed') {
        setScanResult(result);
        setScanning(false);
        setProgress({ percentage: 100, eta_formatted: null, status: 'completed' });
        toast.success('Repository scan completed!');
        loadScanHistory();
      } else {
        // Scan is async, will be tracked via polling
        toast.info('Scan started! Tracking progress...');
      }
    } catch (err) {
      console.error('Scan failed:', err);
      toast.error(err.response?.data?.detail || 'Repository scan failed');
      setScanning(false);
    }
  };
  
  const handleContainerScan = async () => {
    if (!imageName) {
      toast.error('Please enter a container image name');
      return;
    }
    
    setScanning(true);
    setScanResult(null);
    
    try {
      const result = await scanContainer(imageName, registry || null, selectedFramework || null);
      setScanResult(result);
      toast.success('Container scan completed!');
    } catch (err) {
      console.error('Scan failed:', err);
      toast.error(err.response?.data?.detail || 'Container scan failed');
    } finally {
      setScanning(false);
    }
  };
  
  const handleIaCScan = async () => {
    if (!iacContent) {
      toast.error('Please enter or paste IaC configuration');
      return;
    }
    
    setScanning(true);
    setScanResult(null);
    
    try {
      const result = await scanIaC(iacContent, iacType, iacFileName || null, selectedFramework || null);
      setScanResult(result);
      toast.success('IaC scan completed!');
    } catch (err) {
      console.error('Scan failed:', err);
      toast.error(err.response?.data?.detail || 'IaC scan failed');
    } finally {
      setScanning(false);
    }
  };
  
  const resetScan = () => {
    setScanResult(null);
    setFile(null);
    setRepoUrl('');
    setImageName('');
    setIacContent('');
    setCurrentScanId(null);
    setProgress({ percentage: 0, eta_formatted: null });
  };
  
  const loadHistoryScan = async (scanId) => {
    try {
      const scan = await getScan(scanId);
      setScanResult(scan);
      setCurrentScanId(scanId);
    } catch (err) {
      toast.error('Failed to load scan');
    }
  };
  
  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'running': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}`)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">New Vulnerability Scan</h1>
          <p className="text-gray-500">Scan code, dependencies, containers, or infrastructure configurations</p>
        </div>
      </div>
      
      {/* Progress Bar - Shows during scanning */}
      {scanning && (
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                <span className="font-medium text-blue-900">Scanning in progress...</span>
              </div>
              <div className="flex items-center gap-4 text-sm text-blue-700">
                <span className="font-mono">{progress.percentage.toFixed(0)}%</span>
                {progress.eta_formatted && (
                  <span className="flex items-center gap-1">
                    <Timer className="w-4 h-4" />
                    ETA: {progress.eta_formatted}
                  </span>
                )}
              </div>
            </div>
            <Progress value={progress.percentage} className="h-3" />
            <p className="mt-2 text-xs text-blue-600">
              {progress.status === 'running' 
                ? 'Analyzing dependencies and checking for vulnerabilities...' 
                : progress.status === 'pending'
                ? 'Initializing scan...'
                : 'Processing...'}
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Scan Result */}
      {scanResult && (
        <Card className="border-2 border-indigo-200 bg-indigo-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {scanResult.status === 'completed' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : scanResult.status === 'failed' ? (
                  <XCircle className="w-6 h-6 text-red-600" />
                ) : (
                  <Loader2 className="w-6 h-6 text-yellow-600 animate-spin" />
                )}
                <div>
                  <CardTitle>{scanResult.name}</CardTitle>
                  <CardDescription>{formatScanType(scanResult.scan_type)}</CardDescription>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetScan}>New Scan</Button>
                <Button onClick={() => navigate(`${basePath}/scans/${scanResult.id}`)}>
                  View Details
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {scanResult.status === 'completed' && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{scanResult.summary?.critical_count || 0}</p>
                  <p className="text-xs text-gray-500">Critical</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">{scanResult.summary?.high_count || 0}</p>
                  <p className="text-xs text-gray-500">High</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{scanResult.summary?.medium_count || 0}</p>
                  <p className="text-xs text-gray-500">Medium</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{scanResult.summary?.low_count || 0}</p>
                  <p className="text-xs text-gray-500">Low</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-gray-600">{scanResult.summary?.total_packages || 0}</p>
                  <p className="text-xs text-gray-500">Packages</p>
                </div>
              </div>
            )}
            {scanResult.status === 'failed' && (
              <div className="p-4 bg-red-100 rounded-lg">
                <p className="text-red-700">{scanResult.error_message || 'Scan failed'}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Scan Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-4 w-full max-w-xl">
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Upload File
              </TabsTrigger>
              <TabsTrigger value="repository" className="flex items-center gap-2">
                <GitBranch className="w-4 h-4" />
                Repository
              </TabsTrigger>
              <TabsTrigger value="container" className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Container
              </TabsTrigger>
              <TabsTrigger value="iac" className="flex items-center gap-2">
                <FileCode className="w-4 h-4" />
                IaC
              </TabsTrigger>
            </TabsList>
            
            {/* Upload Tab */}
            <TabsContent value="upload" className="space-y-4 mt-6">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${file ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 hover:border-indigo-400'}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
              >
                {file ? (
                  <div className="space-y-2">
                    <CheckCircle className="w-10 h-10 mx-auto text-green-500" />
                    <p className="font-medium">{file.name}</p>
                    <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    <Button variant="outline" size="sm" onClick={() => setFile(null)}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-10 h-10 mx-auto text-gray-400" />
                    <p className="font-medium">Drop a dependency file here</p>
                    <p className="text-sm text-gray-500">
                      Supports: package-lock.json, requirements.txt, composer.lock, etc.
                    </p>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleFileDrop}
                        accept=".json,.txt,.lock,.yaml,.sum"
                      />
                      <span className="text-indigo-600 hover:underline">Browse files</span>
                    </label>
                  </div>
                )}
              </div>
              
              <div className="flex items-center gap-4">
                <select
                  className="border rounded-md px-3 py-2 text-sm"
                  value={selectedFramework}
                  onChange={(e) => setSelectedFramework(e.target.value)}
                >
                  <option value="">Link to Framework (optional)</option>
                  {frameworks.map(fw => (
                    <option key={fw.id} value={fw.id}>{fw.name}</option>
                  ))}
                </select>
              </div>
              
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleUploadScan}
                disabled={!file || scanning}
              >
                {scanning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
                ) : (
                  <><Shield className="w-4 h-4 mr-2" /> Scan for Vulnerabilities</>
                )}
              </Button>
            </TabsContent>
            
            {/* Repository Tab */}
            <TabsContent value="repository" className="space-y-4 mt-6">
              {/* Info banner clarifying that GitHub URLs are scanned via API (no git required) */}
              <div
                className="flex gap-3 items-start rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
                data-testid="litesabre-github-api-banner"
              >
                <CheckCircle className="w-5 h-5 mt-0.5 shrink-0 text-emerald-600" />
                <div>
                  <div className="font-medium">GitHub API scanner active — no git binary required</div>
                  <div className="text-xs mt-0.5 text-emerald-800/80">
                    All <span className="font-mono">github.com/*</span> URLs (including
                    <span className="font-mono"> .git</span> suffixes) are scanned using GitHub's REST API
                    and <span className="font-mono">raw.githubusercontent.com</span>. Works in production
                    environments without git installed. Non-GitHub URLs (GitLab, Bitbucket, etc.) require
                    git to be installed.
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Repository URL</label>
                  <Input
                    data-testid="litesabre-repo-url-input"
                    placeholder="https://github.com/kyutai-labs/moshi.git"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tip: for GitHub, both <span className="font-mono">https://github.com/owner/repo</span> and
                    <span className="font-mono"> https://github.com/owner/repo.git</span> are supported.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Branch</label>
                    <Input
                      placeholder="main"
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Link to Framework</label>
                    <select
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      value={selectedFramework}
                      onChange={(e) => setSelectedFramework(e.target.value)}
                    >
                      <option value="">Select Framework (optional)</option>
                      {frameworks.map(fw => (
                        <option key={fw.id} value={fw.id}>{fw.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleRepoScan}
                disabled={!repoUrl || scanning}
              >
                {scanning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning Repository...</>
                ) : (
                  <><GitBranch className="w-4 h-4 mr-2" /> Scan Repository</>
                )}
              </Button>
            </TabsContent>
            
            {/* Container Tab */}
            <TabsContent value="container" className="space-y-4 mt-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Image Name</label>
                  <Input
                    placeholder="nginx:1.21 or myapp:latest"
                    value={imageName}
                    onChange={(e) => setImageName(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Registry (optional)</label>
                    <Input
                      placeholder="docker.io, gcr.io, etc."
                      value={registry}
                      onChange={(e) => setRegistry(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Link to Framework</label>
                    <select
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      value={selectedFramework}
                      onChange={(e) => setSelectedFramework(e.target.value)}
                    >
                      <option value="">Select Framework (optional)</option>
                      {frameworks.map(fw => (
                        <option key={fw.id} value={fw.id}>{fw.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleContainerScan}
                disabled={!imageName || scanning}
              >
                {scanning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning Container...</>
                ) : (
                  <><Package className="w-4 h-4 mr-2" /> Scan Container Image</>
                )}
              </Button>
            </TabsContent>
            
            {/* IaC Tab */}
            <TabsContent value="iac" className="space-y-4 mt-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Config Type</label>
                    <select
                      className="w-full border rounded-md px-3 py-2 text-sm"
                      value={iacType}
                      onChange={(e) => setIacType(e.target.value)}
                    >
                      <option value="terraform">Terraform</option>
                      <option value="kubernetes">Kubernetes YAML</option>
                      <option value="docker">Dockerfile / Docker Compose</option>
                      <option value="cloudformation">CloudFormation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">File Name (optional)</label>
                    <Input
                      placeholder="main.tf, deployment.yaml"
                      value={iacFileName}
                      onChange={(e) => setIacFileName(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Configuration Content</label>
                  <textarea
                    className="w-full border rounded-md px-3 py-2 text-sm font-mono h-64 resize-none"
                    placeholder="Paste your Terraform, Kubernetes, or Docker configuration here..."
                    value={iacContent}
                    onChange={(e) => setIacContent(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Link to Framework</label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={selectedFramework}
                    onChange={(e) => setSelectedFramework(e.target.value)}
                  >
                    <option value="">Select Framework (optional)</option>
                    {frameworks.map(fw => (
                      <option key={fw.id} value={fw.id}>{fw.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleIaCScan}
                disabled={!iacContent || scanning}
              >
                {scanning ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning Configuration...</>
                ) : (
                  <><FileCode className="w-4 h-4 mr-2" /> Scan IaC Configuration</>
                )}
              </Button>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Supported Scan Types</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 border rounded-lg">
              <Upload className="w-6 h-6 text-indigo-600 mb-2" />
              <h4 className="font-medium">Dependency Scan</h4>
              <p className="text-sm text-gray-500">npm, Python, PHP, Ruby, Go, Rust dependencies</p>
            </div>
            <div className="p-4 border rounded-lg">
              <GitBranch className="w-6 h-6 text-indigo-600 mb-2" />
              <h4 className="font-medium">Repository Scan</h4>
              <p className="text-sm text-gray-500">Clone and scan entire Git repositories</p>
            </div>
            <div className="p-4 border rounded-lg">
              <Package className="w-6 h-6 text-indigo-600 mb-2" />
              <h4 className="font-medium">Container Scan</h4>
              <p className="text-sm text-gray-500">Docker images and base image vulnerabilities</p>
            </div>
            <div className="p-4 border rounded-lg">
              <FileCode className="w-6 h-6 text-indigo-600 mb-2" />
              <h4 className="font-medium">IaC Scan</h4>
              <p className="text-sm text-gray-500">Terraform, Kubernetes, Docker configs</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Scan History Sidebar */}
      {showHistory && scanHistory.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-indigo-600" />
                <CardTitle className="text-lg">Recent Scans</CardTitle>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={loadScanHistory}
                disabled={historyLoading}
              >
                <RefreshCw className={`w-4 h-4 ${historyLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {scanHistory.map((scan) => (
                <div 
                  key={scan.id}
                  className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => loadHistoryScan(scan.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{scan.name || scan.target}</p>
                      <p className="text-xs text-gray-500 truncate">{scan.target}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <Badge className={`text-xs ${getStatusColor(scan.status)}`}>
                        {scan.status}
                      </Badge>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {scan.findings_count || scan.summary?.total_vulnerabilities || 0} findings
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(scan.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-3"
              onClick={() => navigate(`${basePath}/scans`)}
            >
              View All Scans
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LiteSabreScan;
